import React, { useRef, useEffect, useState } from 'react';
import {
  Carousel,
  CarouselCard,
  CarouselNav,
  CarouselNavButton,
  CarouselNavContainer,
  CarouselSlider,
  CarouselViewport,
  makeStyles,
} from '@fluentui/react-components';
import { ProductCard } from '../ProductCard';
import type { Product } from '../../../../services/storeService';

const useStyles = makeStyles({
    carouselSection: {
        width: '100%'
    },

    carouselTitleSection: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '32px',
        paddingLeft: '80px',
        paddingRight: '80px',
        marginBottom: '24px',
        '@media (max-width: 768px)': {
            paddingLeft: '16px',
            paddingRight: '16px',
        },
    },

    carouselTitleContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '8px',
    },

    carouselTitle: {
        color: '#C6A972',
        fontSize: '24px',
        fontFamily: 'Figtree',
        fontWeight: 700,
        lineHeight: '24px',
        letterSpacing: '1.20px',
        wordWrap: 'break-word',
        textAlign: 'left',
        margin: 0,
    },

    carouselSubtitle: {
        color: '#666666',
        fontSize: '48px',
        fontFamily: 'Figtree',
        fontWeight: 400,
        lineHeight: '28px',
        wordWrap: 'break-word',
        textAlign: 'left',
        margin: 0,
        '@media (max-width: 768px)': {
            fontSize: '16px',
            lineHeight: '24px',
        },
    },

    carousel: {
        width: '100%',
    },

    carouselNav: {
        width: '112px',
        height: '48px',
        justifyContent: 'space-between',
        alignItems: 'center',
        display: 'flex',
        '@media (max-width: 768px)': {
            display: 'none',
        },
        '& > div[role="tablist"]': {
            display: 'none',
        },
    },

    carouselCard: {
        width: '280px',
        marginLeft: '0',
        marginRight: '12px',
    },

    carouselSlider: {
        height: '100%',
        '& > *:first-child': {
            marginLeft: '80px',
        },
        '@media (max-width: 768px)': {
            '& > *:first-child': {
                marginLeft: '16px',
            },
        },
    },
});

export interface ProductTagCarouselProps {
    title: string;
    subtitle: string;
    products: Product[];
    category: string;
    onAddToCart: (product: Product) => void;
}

export const ProductTagCarousel: React.FC<ProductTagCarouselProps> = ({
    title,
    subtitle,
    products,
    category,
    onAddToCart
}) => {
    const styles = useStyles();
    const carouselRef = useRef<HTMLDivElement>(null);
    const [showNavigation, setShowNavigation] = useState(true);

    useEffect(() => {
        const checkOverflow = () => {
            if (carouselRef.current) {
                const container = carouselRef.current.querySelector('[role="region"]');
                if (container) {
                    const containerWidth = (container as HTMLElement).clientWidth;
                    const cardWidth = 328; // 280 + margins
                    const visibleCards = Math.floor(containerWidth / cardWidth);
                    setShowNavigation(products.length > visibleCards);
                }
            }
        };

        checkOverflow();
        window.addEventListener('resize', checkOverflow);
        return () => window.removeEventListener('resize', checkOverflow);
    }, [products.length]);

    if (products.length === 0) {
        return (
            <div className={styles.carouselSection}>
                <div className={styles.carouselTitleSection}>
                    <div className={styles.carouselTitleContent}>
                        <h2 className={styles.carouselTitle}>{title}</h2>
                        <p className={styles.carouselSubtitle}>{subtitle}</p>
                    </div>
                </div>
                <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                    No products found for {title}
                </div>
            </div>
        );
    }

    return (
        <div className={styles.carouselSection} ref={carouselRef}>
            <Carousel className={styles.carousel} groupSize={1} circular={false} draggable>
                <div className={styles.carouselTitleSection}>
                    <div className={styles.carouselTitleContent}>
                        <h2 className={styles.carouselTitle}>{title}</h2>
                        <p className={styles.carouselSubtitle}>{subtitle}</p>
                    </div>
                    {showNavigation && (
                        <CarouselNavContainer 
                            className={styles.carouselNav} 
                            layout="inline" 
                            next={{ 'aria-label': 'go to next' }} 
                            prev={{ 'aria-label': 'go to prev' }}
                        >
                            <CarouselNav>
                                {(index) => (
                                    <CarouselNavButton aria-label={`Carousel Nav Button ${index}`} />
                                )}
                            </CarouselNav>
                        </CarouselNavContainer>
                    )}
                </div>
                <CarouselViewport>
                    <CarouselSlider className={styles.carouselSlider}>
                        {products.map((product) => (
                            <CarouselCard autoSize={true} key={`${category}-${product.productId}`} className={styles.carouselCard}>
                                <ProductCard product={product} onAddToCart={onAddToCart} />
                            </CarouselCard>
                        ))}
                    </CarouselSlider>
                </CarouselViewport>
            </Carousel>
        </div>
    );
};

// named-only exports

